import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext'; // Assuming useAuth provides user object
import { Patient } from '../types/firestoreTypes';

// Define the return type of the hook
interface UsePatientDataReturn {
  patient: Patient | null;
  isLoading: boolean;
  error: string | null; // Error will hold the translated string
  refresh: () => void;
}

export const usePatientData = (patientId: string): UsePatientDataReturn => {
  const { t } = useTranslation(); // Get the t function
  const { user } = useAuth(); // Get the current user from AuthContext
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // State holds the translated error string
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey(prevKey => prevKey + 1);
  }, []);

  useEffect(() => {
    // Function to set translated error messages
    const setTranslatedError = (key: string) => {
      setError(t(key));
    };

    const fetchPatient = async () => {
      // Reset state for new fetch
      setIsLoading(true);
      setError(null);
      setPatient(null); // Clear previous patient data

      // Guard clauses for missing user or patientId
      if (!user) {
        console.log('usePatientData: No user found, skipping fetch.');
        // setError('User not authenticated'); // Optionally set an error, but usually handled by routing
        setIsLoading(false);
        return;
      }
      if (!patientId) {
        console.log('usePatientData: No patientId provided, skipping fetch.');
        // setError('No patient ID specified'); // Optionally set an error
        setIsLoading(false);
        return;
      }

      console.log(`usePatientData: Fetching patient with ID: ${patientId}`);

      try {
        const patientDocRef = doc(db, 'patients', patientId);
        const patientDoc = await getDoc(patientDocRef);

        if (!patientDoc.exists()) {
          console.log('usePatientData: Patient document not found.');
          setTranslatedError('hooks.usePatientData.errors.notFound'); // Use key
          setIsLoading(false); // Stop loading even if not found
          return;
        }

        // Construct patient data including the ID
        const patientData = {
          id: patientDoc.id,
          ...(patientDoc.data() as Omit<Patient, 'id'>), // Type assertion
        };

        // Permission check: Ensure the fetched patient belongs to the logged-in therapist
        if (patientData.therapistId !== user.uid) {
          console.warn(
            `usePatientData: Permission denied for user ${user.uid} to view patient ${patientId}`
          );
          setTranslatedError('hooks.usePatientData.errors.permissionDenied'); // Use key
          setPatient(null); // Clear patient data on permission error
          setIsLoading(false); // Stop loading
          return;
        }

        // Set patient data on success
        console.log('usePatientData: Patient data fetched successfully.');
        setPatient(patientData);
      } catch (err) {
        // Handle Firestore or other errors during fetch
        console.error('usePatientData: Error fetching patient:', err);
        setTranslatedError('hooks.usePatientData.errors.loadFailed'); // Use key
      } finally {
        // Ensure loading is always set to false after fetch attempt
        setIsLoading(false);
      }
    };

    fetchPatient();

    // No cleanup needed here as getDoc is a one-time fetch
  }, [user, patientId, t, refreshKey]); // Add t to dependency array

  // Return the state variables
  return { patient, isLoading, error, refresh };
};
